// CS Import — every archived project for one historical client, with the
// contacts, addresses and files commonsku held. A separate historical record:
// never linked to a live Flow client, even one with the same name.
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CS_DOC_LABEL, CS_DOC_ORDER, csDay, type CsClient } from "@/lib/csArchive";
import { CsNotReady, Crumbs, Fact, FileLine, SideCard } from "./parts";

export function CsImportClientPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["cs-archive", "client", id], queryFn: () => api<CsClient>(`/cs-archive/clients/${id}`) });
  if (q.data && !q.data.ready) return <CsNotReady />;
  if (q.isError) return <p className="mt-10 text-[16px] text-red-700">This archived client couldn't be found.</p>;
  if (!q.data) return <p className="mt-10 text-muted">Loading…</p>;
  const { client: c, contacts, addresses, projects, files } = q.data;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-1 pb-16">
      <Crumbs> / {c.name}</Crumbs>
      <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-muted">Historical client · commonsku</p>
          <h1 className="mt-2 text-[40px] font-semibold leading-[1.08] tracking-[-1.2px] text-ink">{c.name}</h1>
          <p className="mt-2 text-[16px] text-muted">{projects.length} project{projects.length === 1 ? "" : "s"} · {csDay(c.created_on)} – {csDay(c.last_activity_on)}</p>
        </div>
        <span className="rounded-md bg-canvas px-3 py-1.5 text-[14px] text-ink">Historical record</span>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <section>
          <h2 className="border-b border-rule pb-3 text-[20px] font-semibold text-ink">All projects</h2>
          <ul>
            {projects.map((p) => (
              <li key={p.id} className="flex items-center gap-4 border-b border-rule py-5">
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-muted">#{p.job_number} · {csDay(p.last_activity_on ?? p.created_on)}</p>
                  <Link to={`/cs-import/projects/${p.id}`} className="mt-1 block text-[18px] font-semibold text-ink hover:underline">{p.name}</Link>
                  <p className="mt-1 text-[14px] text-muted">{[p.latest_stage, p.client_rep_name].filter(Boolean).join(" · ")}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {CS_DOC_ORDER.filter((t) => p.doc_types.includes(t)).map((t) => <span key={t} className="rounded-md border border-rule px-2 py-0.5 text-[12px] text-ink">{CS_DOC_LABEL[t]}</span>)}
                  </div>
                </div>
                <Link to={`/cs-import/projects/${p.id}`} className="inline-flex h-11 shrink-0 items-center rounded-md border border-rule bg-white px-4 text-[15px] font-medium text-ink hover:bg-canvas">View project ↗</Link>
              </li>
            ))}
          </ul>
          {files.length ? (
            <>
              <h2 className="mt-10 border-b border-rule pb-3 text-[20px] font-semibold text-ink">Client files</h2>
              <ul>{files.map((f) => <FileLine key={f.id} f={f} />)}</ul>
            </>
          ) : null}
        </section>

        <div className="space-y-6">
          <SideCard title="The client, as commonsku had it.">
            <Fact label="Historical rep">{c.rep_name}</Fact>
            <Fact label="Primary contact">{c.primary_contact}</Fact>
            <Fact label="Industry">{c.industry}</Fact>
            {c.website ? <Fact label="Website">{c.website}</Fact> : null}
          </SideCard>
          <SideCard title="Contacts">
            {contacts.length ? contacts.map((k) => (
              <div key={k.id} className="border-b border-rule pb-3 last:border-0">
                <p className="font-semibold text-ink">{[k.first_name, k.last_name].filter(Boolean).join(" ") || "—"}{!k.active ? <span className="ml-2 text-[12px] font-normal text-muted">inactive</span> : null}</p>
                <p className="text-[14px] text-muted">{[k.title, k.email, k.phone].filter(Boolean).join(" · ")}</p>
              </div>
            )) : <p className="text-muted">None recorded.</p>}
          </SideCard>
          <SideCard title="Addresses">
            {addresses.length ? addresses.map((a) => (
              <div key={a.id} className="border-b border-rule pb-3 text-[14px] last:border-0">
                <p className="font-semibold text-ink">{a.label ?? "Address"}{!a.active ? <span className="ml-2 text-[12px] font-normal text-muted">inactive</span> : null}</p>
                <p className="text-muted">{[a.company, a.line1, a.line2, [a.city, a.state, a.postal].filter(Boolean).join(", "), a.country].filter(Boolean).join(" · ")}</p>
              </div>
            )) : <p className="text-muted">None recorded.</p>}
          </SideCard>
        </div>
      </div>
    </div>
  );
}
